import PolicyLayout, { Blocks, ContactCard } from "./PolicyLayout";

// About Fender — About page rendered from a structured content array so the
// copy stays easy to maintain without touching JSX layout.

const blocks = [
  { type: "p", text: "Fender is a modern garage management platform built for independent and multi-brand automobile workshops. It helps workshops manage their daily operations from one simple system — including appointments, job cards, vehicle inspections, estimates, spare parts, inventory, invoices, payments and vehicle delivery." },
  { type: "p", text: "Fender is designed around the way Indian workshops actually operate: fast-moving environments, multiple team members, frequent spare-parts requirements, and internet connectivity that may not always be reliable. Our objective is simple: help workshops become more organised, serve customers faster and run their operations more professionally." },

  { type: "h2", text: "Built for Indian Workshops" },
  { type: "p", text: "Many workshop software products are either too complicated, too expensive or designed around workflows that do not match how independent garages actually work. Fender has been built differently. The platform focuses on the essential activities required to run a workshop efficiently while keeping the interface simple for service advisors, mechanics, storekeepers, cashiers and workshop owners." },
  { type: "p", text: "With Fender, workshops can:" },
  { type: "ul", items: [
    "Create and manage Job Cards",
    "Schedule appointments",
    "Record vehicle inspections",
    "Prepare professional estimates",
    "Assign work to mechanics and service advisors",
    "Track repair progress",
    "Manage spare-parts requirements",
    "Maintain basic inventory",
    "Create invoices and record payments",
    "Generate Gate Passes",
    "Maintain customer and vehicle service history",
    "Manage pickup and drop",
    "Send spare-parts enquiries directly to SparesHub",
  ]},

  { type: "h2", text: "Designed for the Entire Workshop Team" },
  { type: "p", text: "Fender is not meant only for the workshop owner. Different members of the workshop team can use the platform based on their responsibilities, including:" },
  { type: "ul", items: [
    "Garage Owners",
    "Branch Managers",
    "Service Advisors",
    "Mechanics",
    "Storekeepers",
    "Purchase Executives",
    "Cashiers",
    "Drivers",
    "Security / Gate Personnel",
    "Account and Audit users",
  ]},
  { type: "p", text: "Role-based access helps ensure that each user sees the information and functions relevant to their work." },

  { type: "h2", text: "Available in 8 Languages" },
  { type: "p", text: "Workshops across India often have teams who are more comfortable working in their regional language. Fender therefore supports: English, Hindi, Marathi, Gujarati, Tamil, Telugu, Kannada and Malayalam." },
  { type: "p", text: "Our aim is to make professional workshop software usable by the entire workshop team, not only by users comfortable with English." },

  { type: "h2", text: "Built for Real-World Connectivity" },
  { type: "p", text: "Workshops do not always operate with perfect internet connectivity. Fender has been designed with this reality in mind. The platform can save work locally and synchronise data when connectivity becomes available again, helping workshops continue working even during weak or intermittent internet conditions. This reduces the risk of lost Job Cards, incomplete inspections or interrupted workshop operations." },

  { type: "h2", text: "Fender + SparesHub" },
  { type: "p", text: "Fender is developed as part of the SparesHub ecosystem. One of the most useful integrations within Fender is the ability to submit spare-parts requirements directly to SparesHub from a Job Card. Instead of preparing the same enquiry again, workshops can send relevant information such as:" },
  { type: "ul", items: [
    "Vehicle details",
    "Required parts",
    "Quantities",
    "Part numbers",
    "Job Card information",
    "Photographs",
  ]},
  { type: "p", text: "directly to the SparesHub team. This helps workshops reduce administrative work and obtain spare-parts quotations more efficiently." },

  { type: "h2", text: "Currently a SparesHub Customer Benefit" },
  { type: "callout", text: "Fender is currently being offered on a complimentary basis to eligible SparesHub customers. It is part of our effort to create more value for workshops that work with SparesHub — not only by supplying spare parts, but also by helping them improve their day-to-day operations." },

  { type: "h2", text: "Accessible Across Devices" },
  { type: "p", text: "Fender is designed for use across Web, Android and iOS. Workshop owners can monitor operations from a computer, while service advisors, mechanics and other team members can work from mobile devices where appropriate." },

  { type: "h2", text: "Our Vision" },
  { type: "p", text: "We believe independent and multi-brand workshops should have access to modern technology that is simple, practical and affordable. Our vision for Fender is to become a dependable operating platform for workshops across India — helping them work faster, stay organised, improve customer experience and grow their business." },
];

export default function About() {
  return (
    <PolicyLayout
      testId="about-page"
      title="Powering Workshops. Driving Growth."
      subtitle="Fender is a modern garage management platform built for the way independent and multi-brand workshops in India actually work."
    >
      <Blocks blocks={blocks} />
      <h2 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] tracking-tight mt-12 mb-4">About the Company</h2>
      <p className="text-[14px] md:text-[15px] text-[#1d3557] leading-relaxed">
        Fender is owned and operated by <span className="notranslate font-semibold">Iradium Automobiles Private Limited</span>, Pune, Maharashtra, India. Fender is a product of the SparesHub ecosystem.
      </p>
      <ContactCard />
    </PolicyLayout>
  );
}
